import { useEffect, useRef } from "react";
import { AppState, AppStateStatus } from "react-native";
import { useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@src/features/tenants";

/**
 * Hook that refetches relevant queries when the app comes to foreground.
 * This ensures data is fresh when users return to the app after it was in the background.
 */
export function useRefetchOnForeground() {
  const queryClient = useQueryClient();
  const { selectedTenant } = useTenant();
  const tenantId = selectedTenant?.id;
  const appState = useRef<AppStateStatus>(AppState.currentState);

  useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextAppState: AppStateStatus) => {
      // App is coming to foreground from background or inactive state
      if (appState.current.match(/inactive|background/) && nextAppState === "active") {
        if (tenantId) {
          queryClient.invalidateQueries({
            queryKey: ["cameras", tenantId],
            exact: false,
          });

          queryClient.invalidateQueries({
            queryKey: ["alarms", tenantId],
            exact: false,
          });

          queryClient.invalidateQueries({
            queryKey: ["activity", tenantId],
            exact: false,
          });

          queryClient.invalidateQueries({
            queryKey: ["camera-contacts", tenantId],
            exact: false,
          });
        }
      }

      appState.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, [queryClient, tenantId]);
}
